import { Button, ButtonMode, ButtonSize, colors, Icon, ICONS, Loader } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { isNil } from 'lodash';
import React from 'react';
import Pluralize from 'react-pluralize';
import { CONTENTTYPE_PERMISSIONS_MAP } from '@creditsights/api-ui-web';
import {
  SEARCH_RESULTS_CATEGORY_BUTTONS,
  SearchResultCategory,
  SearchResultCategoryToContentTypeMap,
} from '../SearchResults.constants';
import styles from './SearchResultsHeader.module.scss';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';

interface SearchResultsHeaderProps {
  searchTerm: string;
  onCategorySelect: (value: SearchResultCategory) => void;
  activeCategory: SearchResultCategory;
  resultsCount?: number;
}

const cx = classNames.bind(styles);

const SearchResultsHeader = ({
  searchTerm,
  onCategorySelect,
  activeCategory,
  resultsCount,
}: SearchResultsHeaderProps): React.ReactElement => {
  const { data: userPermissions } = useFetchUserPermissions();

  const renderTabButton = ({ label, value }: { label: SearchResultCategory; value: SearchResultCategory }) => {
    const tabContentType = SearchResultCategoryToContentTypeMap[value];
    const hasPermissions = userPermissions!.permissions.includes(CONTENTTYPE_PERMISSIONS_MAP[tabContentType!]);
    const icon = tabContentType && !hasPermissions ? ICONS.LockOutline : undefined;

    return (
      <Button
        className={cx('category-btn', { active: value === activeCategory })}
        onClick={() => onCategorySelect(value)}
        key={value}
        mode={ButtonMode.PRIMARY}
        size={ButtonSize.X_SMALL}
        ariaLabel={label}
      >
        {icon ? (
          <Icon
            name={ICONS.LockOutline}
            width={12}
            height={12}
            color={value === activeCategory ? colors['white-color'] : colors.tertiary700}
          />
        ) : undefined}
        {label}
      </Button>
    );
  };

  return (
    <div className={cx('body')}>
      <h2 className={cx('header')} data-testid="search-result-header">
        <>
          <span className={cx('results-prefix')}>Results&nbsp;for&nbsp;</span>
          {searchTerm}
          <span className={cx('results-count')}>
            {isNil(resultsCount) ? (
              <Loader color="tertiary" text={null} />
            ) : (
              resultsCount > 0 && <Pluralize count={resultsCount} singular="Result" plural="Results" />
            )}
          </span>
        </>
      </h2>
      <div className={cx('category-list')}>
        {SEARCH_RESULTS_CATEGORY_BUTTONS.map((button) => renderTabButton(button))}
      </div>
    </div>
  );
};

export default SearchResultsHeader;
